import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  type UrlTree,
} from '@angular/router';
import {
  AuthService,
  MULTIFACTOR_ENROLLMENT_ROUTE,
  MultifactorService,
} from '@principle-theorem/ng-auth';
import { MultifactorOverride } from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, snapshot } from '@principle-theorem/shared';
import { pairwise, timeout } from 'rxjs/operators';
import { OrganisationService } from '../organisation.service';

/**
 * Guards main app routes to enforce multifactor enrolment when the resolved
 * organisation/user settings require it. Uses an in-memory session flag so
 * that the check only runs once per login session — enabling multifactor in
 * workspace or user settings won't immediately redirect the current user;
 * they'll be asked to enrol on their next login instead.
 *
 * The route guard only governs whether unenrolled users are forced to enrol.
 * When a user transitions from "multifactor required" to "multifactor not required"
 * (via per-user multifactorOverride=MultifactorOverride.Disabled OR via org
 * settings.multifactor.enabled=false), the admin action in
 * SecurityComponent / UserEditComponent synchronously calls the
 * disableMultifactorForUsers Cloud Function which wipes their enrolled Firebase
 * multifactor factors via Admin SDK. This guard itself does not invoke the wipe —it
 * only reads the resolved state. As a consequence, users who are unenrolled at
 * the time of state transition have their factors wiped server-side, so they
 * will NOT be challenged by Firebase Auth on subsequent sign-ins.
 *
 * The flag resets automatically when the user logs out, or on page
 * refresh (since it's in-memory).
 */
@Injectable()
export class MultifactorEnforcementGuard {
  private _router = inject(Router);
  private _organisation = inject(OrganisationService);
  private _multifactorService = inject(MultifactorService);
  private _authService = inject(AuthService);
  private _sessionChecked = false;

  constructor() {
    this._authService.authUser$
      .pipe(pairwise(), takeUntilDestroyed())
      .subscribe(([previous, current]) => {
        if (previous && !current) {
          this._sessionChecked = false;
        }
      });
  }

  async canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (this._sessionChecked) {
      return true;
    }

    const claims = await snapshot(
      this._authService.claims$.pipe(timeout(10_000))
    ).catch(() => undefined);
    if (claims?.isManagement) {
      this._sessionChecked = true;
      return true;
    }

    const loginTree = this._router.createUrlTree(['/login']);

    const organisation = await snapshot(
      this._organisation.organisation$.pipe(filterUndefined(), timeout(10_000))
    ).catch(() => undefined);
    if (!organisation) {
      return loginTree;
    }

    const user = await snapshot(
      this._organisation.user$.pipe(filterUndefined(), timeout(10_000))
    ).catch(() => undefined);
    if (!user) {
      return loginTree;
    }

    const orgMultifactorEnabled =
      organisation.settings?.multifactor?.enabled ?? false;
    const userOverride = user.multifactorOverride;
    const multifactorRequired =
      userOverride === MultifactorOverride.Required ||
      (orgMultifactorEnabled && userOverride !== MultifactorOverride.Disabled);
    if (!multifactorRequired) {
      this._sessionChecked = true;
      return true;
    }

    const enrolledFactors = this._multifactorService.getEnrolledFactors();
    const enrolled = enrolledFactors.length > 0;
    if (enrolled) {
      this._sessionChecked = true;
      return true;
    }

    return this._router.createUrlTree([MULTIFACTOR_ENROLLMENT_ROUTE]);
  }
}
